import React, { useMemo, useEffect, useRef, useState, type CSSProperties } from "react";
import {
  motion,
  useInView,
  useScroll,
  useTransform,
  AnimatePresence,
  useMotionValue,
  useSpring,
  animate,
} from "framer-motion";
import { PlusIcon } from "./icons";
import {
  ChevronRight, ArrowRight, Sparkles, UserPlus,
  BookOpen, Utensils, Bus, GraduationCap, Award,
  Home as HomeLucide, CheckCircle2, AlertCircle,
  Sunrise, Sunset, Wallet, Calendar, Shield,
  type LucideIcon,
} from "lucide-react";
import { TriSpherePayTopUp } from "@/components/schoolos-pay-topup";
import {
  type Student, type FeeTerm, type ParentWallet, type Ledger,
  type Checkpoint, type NavSection, type ChildTab, type Insight, childColor,
} from "./tokens";
import { fmtUGX, shortDateTime } from "@/lib/format";

const EASE = [0.32, 0.72, 0, 1] as const;
const SPRING = { type: "spring" as const, stiffness: 300, damping: 28 };

const FONT = "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', system-ui, sans-serif";
const FONT_TIGHT = "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', system-ui, sans-serif";
const FONT_DISPLAY = "'Georgia', 'Times New Roman', serif";

type Notification = { id: string; kind: string; title: string; body: string | null; read_at: string | null; created_at: string };
type CalendarEvent = { id: string; title: string; start_time: string; end_time: string | null; location: string | null };
type StudentWallet = { id: string; owner_id: string; balance_ugx: number; wallet_type: string };

// ── Poster environment: pure white, jet black, gold ────────────────────────
function getEnvironment(hour: number) {
  return {
    label: hour < 12 ? "Morning" : hour < 17 ? "Afternoon" : "Evening",
    icon: hour < 12 ? Sunrise : Sunset,
    bg: "#FFFFFF",
    mesh: [
      { x: 15, y: 10, c: "rgba(201,168,76,0.06)", r: 50 },
      { x: 80, y: 20, c: "rgba(201,168,76,0.04)", r: 45 },
      { x: 50, y: 70, c: "rgba(123,20,22,0.03)", r: 40 },
    ],
    ray: "rgba(201,168,76,0.03)",
    accent: "#C9A84C",
    accentDeep: "#9A7828",
    fg: "#0D0D0D", fgMid: "#3C3530", fgMuted: "#8A8880",
    hairline: "rgba(13,13,13,0.08)", surface: "rgba(255,255,255,0.72)",
  };
}

const GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.006'/%3E%3C/svg%3E\")";

// ═══════════════════════════════════════════════════════════════
// MeshBackground — layered radial gradients + light rays + grain
// ═══════════════════════════════════════════════════════════════
function MeshBackground({ env }: { env: ReturnType<typeof getEnvironment> }) {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0, background: env.bg }}>
      {env.mesh.map((m, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            left: `${m.x}%`, top: `${m.y}%`, width: `${m.r}vw`, height: `${m.r}vw`,
            background: `radial-gradient(circle, ${m.c} 0%, transparent 65%)`,
            filter: "blur(40px)",
          }}
          animate={{ x: [0, 30 * (i % 2 ? 1 : -1), 0], y: [0, 20 * (i % 2 ? -1 : 1), 0], opacity: [0.5, 0.85, 0.5] }}
          transition={{ duration: 20 + i * 5, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}
      <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${env.ray} 0%, transparent 30%)` }} />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.03) 100%)" }} />
      <div className="absolute inset-0" style={{ backgroundImage: GRAIN, backgroundRepeat: "repeat" }} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CursorSpotlight — soft light follows cursor
// ═══════════════════════════════════════════════════════════════
function CursorSpotlight({ accent }: { accent: string }) {
  const x = useMotionValue(-500);
  const y = useMotionValue(-500);
  const sx = useSpring(x, { stiffness: 60, damping: 25 });
  const sy = useSpring(y, { stiffness: 60, damping: 25 });
  useEffect(() => {
    const move = (e: MouseEvent) => { x.set(e.clientX); y.set(e.clientY); };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, [x, y]);
  return (
    <motion.div className="fixed pointer-events-none rounded-full"
      style={{ zIndex: 1, left: sx, top: sy, translateX: "-50%", translateY: "-50%", width: 500, height: 500,
        background: `radial-gradient(circle, ${accent}08 0%, transparent 60%)` }} />
  );
}

// ═══════════════════════════════════════════════════════════════
// AmbientParticles — floating dust motes
// ═══════════════════════════════════════════════════════════════
function AmbientParticles({ color, count = 12 }: { color: string; count?: number }) {
  const particles = useMemo(
    () => Array.from({ length: count }, (_, i) => ({
      id: i, x: Math.random() * 100, y: Math.random() * 100,
      size: 1 + Math.random() * 2.5, duration: 18 + Math.random() * 28,
      delay: Math.random() * 8, opacity: 0.02 + Math.random() * 0.06,
    })),
    [count]
  );
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 1 }}>
      {particles.map(p => (
        <motion.div key={p.id} className="absolute rounded-full"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size, background: color, opacity: p.opacity }}
          animate={{ y: [0, -140, 0], x: [0, 45, 0], opacity: [p.opacity, p.opacity * 0.08, p.opacity] }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ChildCard — engaging status card for each child
// ═══════════════════════════════════════════════════════════════
function ChildCard({
  student, wallet, goChild, accent, fg, fgMid, fgMuted, hairline, surface, delay,
}: {
  student: Student;
  wallet?: StudentWallet;
  goChild: (id: string, tab?: ChildTab) => void;
  accent: string; fg: string; fgMid: string; fgMuted: string; hairline: string; surface: string;
  delay: number;
}) {
  const color = student.color_hex || "#C4A882";
  const statusLabel = student.status_label?.toLowerCase() ?? "at school";
  const initials = student.initials;
  const fullName = student.full_name;
  const firstName = fullName.split(" ")[0];
  const walletBalance = wallet?.balance_ugx ?? student.balance_ugx ?? 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 28, scale: 0.96 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.6, delay, ease: EASE }}
      whileHover={{ y: -6, scale: 1.015 }}
      onClick={() => goChild(student.id)}
      className="cursor-pointer group relative overflow-hidden"
      style={{
        background: surface, backdropFilter: "blur(28px) saturate(1.4)", WebkitBackdropFilter: "blur(28px) saturate(1.4)",
        border: `1px solid ${hairline}`, boxShadow: "0 2px 8px rgba(0,0,0,0.03), 0 20px 56px -24px rgba(0,0,0,0.08)",
      }}
    >
      {/* Color top stripe */}
      <div className="h-1" style={{ background: `linear-gradient(90deg, ${color}, ${color}80)` }} />

      <div className="p-5 sm:p-6">
        {/* Header: avatar + name */}
        <div className="flex items-center gap-4">
          <motion.div
            whileHover={{ scale: 1.08 }}
            transition={SPRING}
            className="relative shrink-0 flex items-center justify-center"
            style={{
              width: 56, height: 56, borderRadius: 16,
              background: `linear-gradient(145deg, ${color}28 0%, ${color}08 100%)`,
              border: `1px solid ${color}25`,
            }}
          >
            <span className="text-lg font-black" style={{ color: "#1C1917", fontFamily: FONT, letterSpacing: "-0.03em" }}>
              {initials}
            </span>
            {/* Status dot */}
            <div className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center"
              style={{ width: 14, height: 14, borderRadius: 999, background: "#fff", border: `1.5px solid ${color}` }}>
              <motion.div
                animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                style={{ width: 7, height: 7, borderRadius: 999, background: student.status_dot === "green" ? "#5C8A6A" : "#C4A882" }}
              />
            </div>
          </motion.div>

          <div className="min-w-0 flex-1">
            <h3 className="truncate text-base font-black" style={{ color: fg, fontFamily: FONT, letterSpacing: "-0.02em" }}>
              {fullName}
            </h3>
            <p className="text-[11px] font-semibold" style={{ color: fgMuted, fontFamily: FONT }}>
              {student.class_label} {student.school_name ? `· ${student.school_name}` : ""}
            </p>
          </div>

          <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1"
            style={{ color: accent }} strokeWidth={2} />
        </div>

        {/* Status line */}
        <div className="mt-4 flex items-center gap-2">
          <motion.div
            animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.85, 0.5] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            className="shrink-0"
            style={{ width: 6, height: 6, borderRadius: 999, background: accent, boxShadow: `0 0 10px ${accent}60` }}
          />
          <span className="text-xs font-bold capitalize" style={{ color: fgMid, fontFamily: FONT }}>
            {statusLabel}
            {student.status_location ? <span className="font-medium" style={{ color: fgMuted }}> · {student.status_location}</span> : null}
          </span>
        </div>

        {/* Quick stats */}
        <div className="mt-4 grid grid-cols-3 gap-3" style={{ borderTop: `1px solid ${hairline}`, paddingTop: 16 }}>
          <div className="flex flex-col gap-0.5">
            <span className="text-[9px] font-bold uppercase tracking-[0.12em]" style={{ color: fgMuted, fontFamily: FONT }}>Meals</span>
            <span className="text-sm font-black" style={{ color: fg, fontFamily: FONT_TIGHT, letterSpacing: "-0.02em" }}>
              {fmtUGX(walletBalance)}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[9px] font-bold uppercase tracking-[0.12em]" style={{ color: fgMuted, fontFamily: FONT }}>Transport</span>
            <span className="text-sm font-black truncate" style={{ color: fg, fontFamily: FONT_TIGHT, letterSpacing: "-0.02em" }}>
              {student.bus_route ?? "—"}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[9px] font-bold uppercase tracking-[0.12em]" style={{ color: fgMuted, fontFamily: FONT }}>Type</span>
            <span className="text-sm font-black" style={{ color: fg, fontFamily: FONT_TIGHT, letterSpacing: "-0.02em" }}>
              {student.boarding ? "Boarder" : "Day"}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════
// QuickActionTile — compact action button with icon
// ═══════════════════════════════════════════════════════════════
function QuickActionTile({
  icon: Icon, label, onClick, accent, fg, fgMuted, hairline, surface, delay,
}: {
  icon: LucideIcon; label: string; onClick: () => void;
  accent: string; fg: string; fgMuted: string; hairline: string; surface: string;
  delay: number;
}) {
  return (
    <motion.button
      initial={{ opacity: 0, y: 18, scale: 0.95 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "-20px" }}
      transition={{ duration: 0.5, delay, ease: EASE }}
      whileHover={{ y: -4, scale: 1.03 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="flex flex-col items-center gap-2 p-4 cursor-pointer"
      style={{
        background: surface, backdropFilter: "blur(28px) saturate(1.4)", WebkitBackdropFilter: "blur(28px) saturate(1.4)",
        border: `1px solid ${hairline}`, boxShadow: "0 2px 8px rgba(0,0,0,0.03), 0 12px 40px -20px rgba(0,0,0,0.06)",
      }}
    >
      <div className="flex items-center justify-center" style={{ width: 40, height: 40, borderRadius: 12, background: `${accent}0F` }}>
        <Icon className="h-4.5 w-4.5" style={{ color: accent }} strokeWidth={1.8} />
      </div>
      <span className="text-[10px] font-bold uppercase tracking-[0.1em]" style={{ color: fg, fontFamily: FONT }}>{label}</span>
    </motion.button>
  );
}

// ═══════════════════════════════════════════════════════════════
// StatPill — compact stat display
// ═══════════════════════════════════════════════════════════════
function StatPill({
  label, value, icon: Icon, color, fg, fgMuted, hairline, delay,
}: {
  label: string; value: string; icon: LucideIcon; color: string;
  fg: string; fgMuted: string; hairline: string; delay: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -16 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-20px" }}
      transition={{ duration: 0.5, delay, ease: EASE }}
      className="flex items-center gap-3 py-3"
      style={{ borderBottom: `1px solid ${hairline}` }}
    >
      <div className="flex items-center justify-center shrink-0"
        style={{ width: 32, height: 32, borderRadius: 10, background: `${color}12` }}>
        <Icon className="h-4 w-4" style={{ color }} strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-[9px] font-bold uppercase tracking-[0.12em] block" style={{ color: fgMuted, fontFamily: FONT }}>{label}</span>
        <span className="text-sm font-black" style={{ color: fg, fontFamily: FONT_TIGHT, letterSpacing: "-0.02em" }}>{value}</span>
      </div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════
// RiverTimeline — flowing river of today's events
// ═══════════════════════════════════════════════════════════════
function RiverTimeline({
  events, accent, fg, fgMid, fgMuted, hairline,
}: {
  events: { time: string; label: string; icon: LucideIcon; status: "done" | "active" | "upcoming" }[];
  accent: string; fg: string; fgMid: string; fgMuted: string; hairline: string;
}) {
  return (
    <div className="relative">
      <div className="absolute left-[19px] top-2 bottom-2 w-px overflow-hidden">
        <motion.div className="absolute inset-0"
          style={{ background: `linear-gradient(180deg, ${accent}00 0%, ${accent}45 20%, ${accent}55 50%, ${accent}45 80%, ${accent}00 100%)` }}
          animate={{ y: ["-100%", "100%"] }} transition={{ duration: 6, repeat: Infinity, ease: "linear" }} />
        <div className="absolute inset-0" style={{ background: hairline }} />
      </div>
      <div className="space-y-5">
        {events.map((ev, i) => {
          const isDone = ev.status === "done";
          const isActive = ev.status === "active";
          return (
            <motion.div key={i} initial={{ opacity: 0, x: -14 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-30px" }} transition={{ delay: i * 0.07, ease: EASE }} className="flex items-center gap-4 pl-0">
              <div className="relative shrink-0" style={{ width: 40, zIndex: 2 }}>
                <motion.div initial={{ scale: 0.5 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.07, ease: EASE }}
                  className="rounded-full flex items-center justify-center"
                  style={{ width: isActive ? 32 : 24, height: isActive ? 32 : 24,
                    background: isActive ? accent : isDone ? "#E5E0D8" : "transparent",
                    border: `1px solid ${isActive ? accent : hairline}`, boxShadow: isActive ? `0 0 18px ${accent}45` : "none" }}>
                  {isActive ? <ev.icon className="h-3.5 w-3.5 text-white" /> : isDone ? <CheckCircle2 className="h-3 w-3" style={{ color: "#B5B0A8" }} strokeWidth={1.5} /> : <div className="h-1.5 w-1.5 rounded-full" style={{ background: fgMuted }} />}
                </motion.div>
                {isActive && <motion.div animate={{ scale: [1, 2], opacity: [0.35, 0] }} transition={{ duration: 2.5, repeat: Infinity, ease: "easeOut" }}
                  className="absolute inset-0 rounded-full" style={{ border: `1.5px solid ${accent}` }} />}
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-[10px] font-bold uppercase tracking-[0.1em]" style={{ color: fgMuted, fontFamily: FONT }}>{ev.time}</span>
                <p className="text-sm font-semibold" style={{ color: isActive ? fg : isDone ? fgMid : fgMuted, opacity: isDone ? 0.65 : 1, fontFamily: FONT, letterSpacing: "-0.02em" }}>{ev.label}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SectionLabel — cinematic section header with gradient
// ═══════════════════════════════════════════════════════════════
function SectionLabel({ children, accent, accentDeep }: { children: React.ReactNode; accent: string; accentDeep: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.5, ease: EASE }} className="flex items-center gap-3 mb-5">
      <motion.span className="h-px" style={{ background: accent }}
        initial={{ width: 0 }} whileInView={{ width: 20 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: EASE }} />
      <span className="text-[10px] font-bold uppercase tracking-[0.2em]"
        style={{ fontFamily: FONT, backgroundImage: `linear-gradient(135deg, ${accentDeep} 0%, ${accent} 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
        {children}
      </span>
    </motion.div>
  );
}

// ═════════════════════════════════════════════════ pulsing paragraph
function PulseParagraph({ text, fg, fgMid, fgMuted }: { text: string; fg: string; fgMid: string; fgMuted: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const words = text.split(" ");
  return (
    <p ref={ref} className="text-lg sm:text-xl lg:text-2xl leading-[1.4] text-center font-semibold"
      style={{ color: fg, fontFamily: FONT_TIGHT, letterSpacing: "-0.03em" }}>
      {words.map((word, i) => (
        <motion.span key={i} initial={{ opacity: 0, y: 16, filter: "blur(6px)" }} animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
          transition={{ delay: i * 0.045, duration: 0.6, ease: EASE }}>{word}{" "}</motion.span>
      ))}
    </p>
  );
}

// ═══════════════════════════════════════════════════════════════
// AnimatedNumber — odometer-style counter
// ═══════════════════════════════════════════════════════════════
function AnimatedNumber({ value, format, fg }: { value: number; format: (n: number) => string; fg: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, { duration: 1.4, ease: EASE, onUpdate: v => setDisplay(Math.round(v)) });
    return () => controls.stop();
  }, [inView, value]);
  return <span ref={ref} style={{ color: fg, fontFamily: FONT_TIGHT, letterSpacing: "-0.03em" }}>{format(display)}</span>;
}

// ═══════════════════════════════════════════════════════════════
// HomeView — The Living Family Home (redesigned)
// ═══════════════════════════════════════════════════════════════
export function HomeView({
  students, fees, parentWallet, ledger, checkpoints, outstandingFees,
  go, goChild, greetText, firstName, greetIcon, setActiveModal, walletId, onAddChild,
  insights, notifications, calendarEvents, studentWallets,
}: {
  students: Student[];
  fees: FeeTerm[];
  parentWallet: ParentWallet | null | undefined;
  ledger: Ledger[];
  checkpoints: Checkpoint[];
  outstandingFees: number;
  go: (s: NavSection) => void;
  goChild: (id: string, tab?: ChildTab) => void;
  greetText: string;
  firstName: string;
  greetIcon: string;
  setActiveModal: (m: string | null) => void;
  walletId?: string;
  onAddChild?: () => void;
  insights: Insight[];
  notifications: Notification[];
  calendarEvents: CalendarEvent[];
  studentWallets: StudentWallet[];
}) {
  const now = new Date();
  const h = now.getHours();
  const env = getEnvironment(h);
  const timeOfDay = h < 12 ? "morning" : h < 17 ? "afternoon" : "evening";

  const walletBalance = parentWallet?.balance_ugx ?? 0;
  const upcomingEvents = calendarEvents.filter(e => new Date(e.start_time) >= now).slice(0, 3);
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const todaySpent = ledger.filter(t => t.direction === "debit" && new Date(t.occurred_at) >= todayStart).reduce((s, t) => s + t.amount_ugx, 0);
  const txns = ledger.slice(0, 4);

  const riverEvents = useMemo(() => {
    if (students.length === 0) return [];
    type E = { time: string; label: string; icon: LucideIcon; status: "done" | "active" | "upcoming" };
    const events: E[] = [];
    if (timeOfDay === "morning") {
      events.push({ time: "06:55", label: "Boarded Bus", icon: Bus, status: "done" });
      events.push({ time: "07:40", label: "Arrived at School", icon: GraduationCap, status: "active" });
      events.push({ time: "08:00", label: "Morning Attendance", icon: CheckCircle2, status: "upcoming" as const });
      events.push({ time: "10:30", label: "Mathematics Lesson", icon: BookOpen, status: "upcoming" });
    } else if (timeOfDay === "afternoon") {
      events.push({ time: "08:00", label: "Morning Attendance", icon: CheckCircle2, status: "done" });
      events.push({ time: "12:40", label: "Lunch Purchased", icon: Utensils, status: "done" });
      events.push({ time: "15:20", label: "Sports Practice", icon: Award, status: "active" });
      events.push({ time: "17:10", label: "Bus Departs School", icon: Bus, status: "upcoming" });
    } else {
      events.push({ time: "08:00", label: "Attendance Recorded", icon: CheckCircle2, status: "done" });
      events.push({ time: "15:20", label: "Sports Practice", icon: Award, status: "done" });
      events.push({ time: "17:10", label: "Arrived Home Safely", icon: HomeLucide, status: "done" });
      events.push({ time: "19:00", label: "Homework Session", icon: BookOpen, status: "active" });
    }
    return events;
  }, [students, timeOfDay]);

  const allocatedTotal = studentWallets.reduce((s, w) => s + w.balance_ugx, 0);
  const availableTotal = walletBalance - Math.round(walletBalance * 0.6);
  const forecastDays = walletBalance > 0 ? Math.max(1, Math.floor(walletBalance / Math.max(1, todaySpent || 5000))) : 0;

  const payStudents = students.map(s => ({ id: s.id, full_name: s.full_name, initials: s.initials, color_hex: childColor(students, s.id) }));
  const payFees = fees.map(f => ({ student_id: f.student_id, total_ugx: f.total_ugx, paid_ugx: f.paid_ugx }));
  const payTrigger = (label: string) => (
    <TriSpherePayTopUp walletId={walletId} students={payStudents} fees={payFees} trigger={
      <button className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-[0.12em] transition-all hover:opacity-90"
        style={{ background: "#0D0D0D", color: "#fff", fontFamily: FONT }}>
        <PlusIcon className="h-3.5 w-3.5" /> {label}
      </button>
    } />
  );

  const hasChildren = students.length > 0;
  const greetingWords = `${greetText}, ${firstName}.`.split(" ");

  return (
    <div className="relative" style={{ minHeight: "100vh" }}>
      <MeshBackground env={env} />
      <CursorSpotlight accent={env.accent} />
      <AmbientParticles color={env.accent} count={10} />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" style={{ zIndex: 2 }}>

        {/* ── HERO GREETING ── */}
        <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, ease: EASE }}
          className="pt-10 sm:pt-14 lg:pt-16 pb-6">

          {/* Time badge */}
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6 }}
            className="flex items-center justify-center gap-3 mb-5">
            <env.icon className="h-3.5 w-3.5" style={{ color: env.accent }} strokeWidth={1.5} />
            <span className="text-[10px] font-bold uppercase tracking-[0.22em]"
              style={{ color: env.accent, fontFamily: FONT }}>{env.label}</span>
            <span className="h-px w-6" style={{ background: `${env.accent}40` }} />
            <span className="text-[10px] font-semibold" style={{ color: env.fgMuted, fontFamily: FONT }}>
              {now.toLocaleTimeString("en-UG", { hour: "numeric", minute: "2-digit" })}
            </span>
          </motion.div>

          {/* Hero greeting */}
          <h1 className="text-center font-black tracking-[-0.04em] leading-[0.88]"
            style={{ fontFamily: FONT, color: env.fg, fontSize: "clamp(2.5rem, 8vw, 5rem)" }}>
            {greetingWords.map((word, i) => (
              <motion.span key={i} initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + i * 0.08, duration: 0.6, ease: EASE }} className="inline-block">{word}&nbsp;</motion.span>
            ))}
          </h1>

          {/* Gold accent sub-headline */}
          <motion.h2 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.6, ease: EASE }}
            className="text-center font-black italic tracking-[-0.04em] leading-[0.88]"
            style={{ fontFamily: FONT_DISPLAY, color: env.accent, fontSize: "clamp(1.6rem, 5.5vw, 3.5rem)" }}>
            Everything is ready.
          </motion.h2>

          {/* Summary line */}
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}
            className="text-center text-sm mt-4 mb-2 max-w-md mx-auto"
            style={{ color: env.fgMuted, fontFamily: FONT_DISPLAY, fontStyle: "italic" }}>
            {hasChildren
              ? "Your family's day is running smoothly. Every payment tracked, every child accounted for."
              : "Welcome to your family's living environment. Link your child to begin."}
          </motion.p>

          {/* Gold rule */}
          <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.4, duration: 0.5, ease: EASE }}
            className="mx-auto mt-4" style={{ height: 2, width: 40, background: env.accent, transformOrigin: "center" }} />
        </motion.section>

        {/* ── CHILD STATUS CARDS ── */}
        {hasChildren ? (
          <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
            className="py-6">
            <SectionLabel accent={env.accent} accentDeep={env.accentDeep}>Your Children</SectionLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              {students.map((s, i) => (
                <ChildCard key={s.id} student={s} wallet={studentWallets.find(w => w.owner_id === s.id)} goChild={goChild}
                  accent={env.accent} fg={env.fg} fgMid={env.fgMid} fgMuted={env.fgMuted} hairline={env.hairline} surface={env.surface}
                  delay={i * 0.1} />
              ))}
              {/* Add child card */}
              {onAddChild && (
                <motion.button
                  initial={{ opacity: 0, y: 28, scale: 0.96 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.6, delay: students.length * 0.1, ease: EASE }}
                  whileHover={{ y: -6, scale: 1.015 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onAddChild}
                  className="flex flex-col items-center justify-center gap-3 p-8 cursor-pointer min-h-[180px]"
                  style={{
                    background: "transparent", border: `1.5px dashed ${env.hairline}`,
                  }}
                >
                  <div className="flex items-center justify-center"
                    style={{ width: 48, height: 48, borderRadius: 14, background: `${env.accent}0A`, border: `1px solid ${env.accent}25` }}>
                    <UserPlus className="h-5 w-5" style={{ color: env.accent }} strokeWidth={1.8} />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-[0.14em]" style={{ color: env.fgMuted, fontFamily: FONT }}>
                    Add Another Child
                  </span>
                </motion.button>
              )}
            </div>
          </motion.section>
        ) : (
          <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="py-12 text-center">
            <motion.button onClick={() => onAddChild?.()}
              whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-2 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.18em] transition-all"
              style={{ background: env.fg, color: "#fff", fontFamily: FONT }}>
              <UserPlus className="h-3.5 w-3.5" /> Link Your First Child
            </motion.button>
          </motion.section>
        )}

        {/* ── PULSE SUMMARY ── */}
        {hasChildren && (
          <motion.section initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="py-8">
            <div className="max-w-xl mx-auto text-center">
              <PulseParagraph
                text={`Everything is running smoothly. ${students.slice(0, 2).map(s => s.full_name.split(" ")[0]).join(" and ")} attended every lesson. ${students.some(s => s.bus_route) ? "Transport is on schedule. " : ""}Lunch has been purchased.`}
                fg={env.fg} fgMid={env.fgMid} fgMuted={env.fgMuted} />
              <div className="flex flex-wrap justify-center gap-4 mt-5">
                {outstandingFees > 0 && (
                  <button onClick={() => go("finance")} className="text-sm font-bold transition-opacity hover:opacity-70"
                    style={{ color: env.accent, textDecoration: "underline", textUnderlineOffset: "4px", textDecorationThickness: "1px", fontFamily: FONT }}>
                    A fees installment of {fmtUGX(outstandingFees)} is due.
                  </button>
                )}
                <button onClick={() => go("consent")} className="text-sm font-bold transition-opacity hover:opacity-70"
                  style={{ color: env.accent, textDecoration: "underline", textUnderlineOffset: "4px", textDecorationThickness: "1px", fontFamily: FONT }}>
                  One consent requires your approval.
                </button>
              </div>
            </div>
          </motion.section>
        )}

        {/* ── QUICK ACTIONS ── */}
        {hasChildren && (
          <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: EASE }} className="py-6">
            <SectionLabel accent={env.accent} accentDeep={env.accentDeep}>Quick Actions</SectionLabel>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 sm:gap-4">
              <QuickActionTile icon={Wallet} label="Finance" onClick={() => go("finance")} delay={0} accent={env.accent} fg={env.fg} fgMuted={env.fgMuted} hairline={env.hairline} surface={env.surface} />
              <QuickActionTile icon={Calendar} label="Calendar" onClick={() => go("calendar")} delay={0.05} accent={env.accent} fg={env.fg} fgMuted={env.fgMuted} hairline={env.hairline} surface={env.surface} />
              <QuickActionTile icon={BookOpen} label="Documents" onClick={() => go("documents")} delay={0.1} accent={env.accent} fg={env.fg} fgMuted={env.fgMuted} hairline={env.hairline} surface={env.surface} />
              <QuickActionTile icon={Bus} label="Transport" onClick={() => goChild(students[0]?.id, "transport")} delay={0.15} accent={env.accent} fg={env.fg} fgMuted={env.fgMuted} hairline={env.hairline} surface={env.surface} />
              <QuickActionTile icon={Sparkles} label="Insights" onClick={() => go("family")} delay={0.2} accent={env.accent} fg={env.fg} fgMuted={env.fgMuted} hairline={env.hairline} surface={env.surface} />
              <QuickActionTile icon={Shield} label="Consents" onClick={() => go("consent")} delay={0.25} accent={env.accent} fg={env.fg} fgMuted={env.fgMuted} hairline={env.hairline} surface={env.surface} />
            </div>
          </motion.section>
        )}

        {/* ── TREASURY + TIMELINE ── */}
        {hasChildren && (
          <motion.section initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: EASE }} className="py-8">
            <SectionLabel accent={env.accent} accentDeep={env.accentDeep}>Treasury & Day</SectionLabel>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

              {/* Treasury card */}
              <div className="p-5 sm:p-6"
                style={{ background: env.surface, backdropFilter: "blur(28px) saturate(1.4)", WebkitBackdropFilter: "blur(28px) saturate(1.4)",
                  border: `1px solid ${env.hairline}`, boxShadow: "0 2px 8px rgba(0,0,0,0.03), 0 20px 56px -24px rgba(0,0,0,0.08)" }}>
                <div className="flex items-center gap-2 mb-4">
                  <Wallet className="h-3.5 w-3.5" style={{ color: env.accent }} strokeWidth={1.5} />
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: env.fgMuted, fontFamily: FONT }}>Wallet</span>
                </div>
                <p className="text-3xl font-extrabold mb-4" style={{ color: env.fg, fontFamily: FONT_TIGHT, letterSpacing: "-0.03em" }}>
                  <AnimatedNumber value={walletBalance} format={fmtUGX} fg={env.fg} />
                </p>
                <div>
                  <StatPill label="Available" value={fmtUGX(availableTotal)} icon={Wallet} color={env.accent} fg={env.fg} fgMuted={env.fgMuted} hairline={env.hairline} delay={0} />
                  <StatPill label="Allocated to children" value={fmtUGX(allocatedTotal)} icon={GraduationCap} color="#5C8A6A" fg={env.fg} fgMuted={env.fgMuted} hairline={env.hairline} delay={0.05} />
                  <StatPill label="Spending forecast" value={`${forecastDays} days`} icon={Calendar} color="#7B1416" fg={env.fg} fgMuted={env.fgMuted} hairline={env.hairline} delay={0.1} />
                </div>
                <div className="flex items-center gap-3 mt-4 pt-4" style={{ borderTop: `1px solid ${env.hairline}` }}>
                  {payTrigger("Top Up")}
                  {outstandingFees > 0 && (
                    <button onClick={() => go("finance")}
                      className="inline-flex items-center gap-1.5 text-xs font-bold transition-opacity hover:opacity-70" style={{ color: env.accent, fontFamily: FONT }}>
                      <AlertCircle className="h-3.5 w-3.5" /> {fmtUGX(outstandingFees)} due
                    </button>
                  )}
                </div>
              </div>

              {/* Timeline card */}
              {riverEvents.length > 0 && (
                <div className="p-5 sm:p-6"
                  style={{ background: env.surface, backdropFilter: "blur(28px) saturate(1.4)", WebkitBackdropFilter: "blur(28px) saturate(1.4)",
                    border: `1px solid ${env.hairline}`, boxShadow: "0 2px 8px rgba(0,0,0,0.03), 0 20px 56px -24px rgba(0,0,0,0.08)" }}>
                  <div className="flex items-center gap-2 mb-4">
                    <motion.span animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                      className="rounded-full" style={{ width: 6, height: 6, background: env.accent, boxShadow: `0 0 8px ${env.accent}80` }} />
                    <span className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: env.fgMuted, fontFamily: FONT }}>Today's Flow</span>
                  </div>
                  <RiverTimeline events={riverEvents} accent={env.accent} fg={env.fg} fgMid={env.fgMid} fgMuted={env.fgMuted} hairline={env.hairline} />
                </div>
              )}
            </div>
          </motion.section>
        )}

        {/* ── UPCOMING EVENTS + RECENT ACTIVITY ── */}
        {(upcomingEvents.length > 0 || txns.length > 0) && hasChildren && (
          <motion.section initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: EASE }} className="py-8">
            <SectionLabel accent={env.accent} accentDeep={env.accentDeep}>Coming Up & Recent</SectionLabel>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

              {/* Upcoming events */}
              {upcomingEvents.length > 0 && (
                <div className="p-5 sm:p-6"
                  style={{ background: env.surface, backdropFilter: "blur(28px) saturate(1.4)", WebkitBackdropFilter: "blur(28px) saturate(1.4)",
                    border: `1px solid ${env.hairline}`, boxShadow: "0 2px 8px rgba(0,0,0,0.03), 0 20px 56px -24px rgba(0,0,0,0.08)" }}>
                  <div className="flex items-center gap-2 mb-4">
                    <Calendar className="h-3.5 w-3.5" style={{ color: env.accent }} strokeWidth={1.5} />
                    <span className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: env.fgMuted, fontFamily: FONT }}>Planner</span>
                  </div>
                  <div className="space-y-3">
                    {upcomingEvents.slice(0, 3).map(ev => {
                      const d = new Date(ev.start_time);
                      const today = new Date(); today.setHours(0, 0, 0, 0);
                      const evDay = new Date(d); evDay.setHours(0, 0, 0, 0);
                      const diff = Math.round((evDay.getTime() - today.getTime()) / 86400000);
                      const when = diff === 0 ? "Today" : diff === 1 ? "Tomorrow" : d.toLocaleDateString("en-UG", { weekday: "short", month: "short", day: "numeric" });
                      return (
                        <div key={ev.id} className="flex items-baseline gap-3">
                          <span className="text-[10px] font-bold w-16 shrink-0 uppercase tracking-[0.1em]" style={{ color: env.fgMuted, fontFamily: FONT }}>{when}</span>
                          <span className="text-sm font-semibold" style={{ color: env.fg, fontFamily: FONT, letterSpacing: "-0.02em" }}>{ev.title}</span>
                        </div>
                      );
                    })}
                  </div>
                  <button onClick={() => go("calendar")}
                    className="mt-4 inline-flex items-center gap-1 text-xs font-bold transition-opacity hover:opacity-70" style={{ color: env.accent, fontFamily: FONT }}>
                    Full calendar <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
              )}

              {/* Recent activity */}
              {txns.length > 0 && (
                <div className="p-5 sm:p-6"
                  style={{ background: env.surface, backdropFilter: "blur(28px) saturate(1.4)", WebkitBackdropFilter: "blur(28px) saturate(1.4)",
                    border: `1px solid ${env.hairline}`, boxShadow: "0 2px 8px rgba(0,0,0,0.03), 0 20px 56px -24px rgba(0,0,0,0.08)" }}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Wallet className="h-3.5 w-3.5" style={{ color: env.accent }} strokeWidth={1.5} />
                      <span className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: env.fgMuted, fontFamily: FONT }}>Activity</span>
                    </div>
                    <button onClick={() => go("finance")} className="text-xs font-bold flex items-center gap-1 transition-opacity hover:opacity-70" style={{ color: env.fgMid, fontFamily: FONT }}>
                      All <ChevronRight className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="space-y-0">
                    {txns.map((tx, i) => {
                      const isCredit = tx.direction === "credit";
                      return (
                        <motion.div key={tx.id} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06, ease: EASE }}
                          className="flex items-center gap-3 py-3" style={{ borderBottom: i < txns.length - 1 ? `1px solid ${env.hairline}` : "none" }}>
                          <span className="text-[10px] font-semibold w-16 shrink-0" style={{ color: env.fgMuted, fontFamily: FONT }}>{shortDateTime(tx.occurred_at)}</span>
                          <span className="text-sm font-semibold flex-1 truncate" style={{ color: env.fg, fontFamily: FONT, letterSpacing: "-0.02em" }}>{tx.title}</span>
                          <span className="text-sm font-bold shrink-0" style={{ color: isCredit ? env.accent : env.fg, fontFamily: FONT }}>
                            {isCredit ? "+" : "−"}{fmtUGX(tx.amount_ugx)}
                          </span>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </motion.section>
        )}

        <div className="h-16 sm:h-20" />
      </div>
    </div>
  );
}
